import React, {useState} from "react";
import {message, Upload, Dropdown, Menu} from "antd";
import moment from "moment";
import {
    UploadOutlined,
    DownloadOutlined,
    PlusOutlined,
    DownOutlined,
    FileExcelOutlined,
    AuditOutlined
} from "@ant-design/icons";
import {useTranslation} from "react-i18next";
import {useHistory} from "react-router-dom";
import {CustomButton} from "../../_defaultComponents/CustomButton/CustomButton";
import {useStore} from "../../../hooks/useStore";
import {b64DecodeToUrl} from "../../../utils/helpers";
import ShiftEntryModal from "./ShiftEntryModal/ShiftEntryModal";
import {ButtonsRow, WarningButton} from "./ComponentStyles";
import {isPoolWorkType} from "../../../constants/shiftWorkTypes";

// Havuz ve dedike için tek şablon kullanılıyor; hangi dosya olacağı backend tarafında
// netleşince yalnız bu sabit değişir.
// (Eski kaynaklar: public/import-templates/shift-template.xlsx ve S3 vardiya-taslak.xlsx)
const SHIFT_TEMPLATE_URL = `${process.env.PUBLIC_URL}/import-templates/shift-template.xlsx`;

const downloadFile = (href, fileName) => {
    const link = document.createElement("a");
    link.href = href;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    setTimeout(() => window.URL.revokeObjectURL(link), 200);
};

/**
 * Vardiya Yönetimi buton satırı. Havuz ve dedike ayrımı yapmadan her iki alt firma tipinde
 * de aynı butonlar gösterilir.
 */
const ShiftManagementActions = ({filters, skip, limit, setReloadData, workType}) => {
    const {t} = useTranslation();
    const stores = useStore();
    const history = useHistory();
    const [importLoading, setImportLoading] = useState(false);
    const [isShiftModalOpen, setIsShiftModalOpen] = useState(false);

    // İtirazlar sayfasının rol listesiyle aynı (bkz. routes/index.js "/reports/shift-score/objections").
    // Yetkisi olmayan rolde butonu göstermek AuthChecker'a takılan bir tıklama üretirdi.
    const canSeeObjections = [
        "admin",
        "superadmin",
        "cUser",
        "oUser",
        "bm",
        "agency",
        "sy",
        "fieldManager",
        "operationSupport"
    ].includes(stores.meStore.role);

    // İçe/dışa aktarma tek bir "Excel İşlemleri" yetkisinde birleştirildi.
    const canExport = stores.meStore.checkPermission("shiftManagement.excelActions");
    const canImport = stores.meStore.checkPermission("shiftManagement.excelActions");

    // Satır bazlı hatalar hata excel'i olarak dönüyor (Eklenti/Kesinti yüklemesiyle aynı akış):
    // dosya otomatik indirilir, kullanıcıya hangi satırda ne hata olduğu o dosyadan gösterilir.
    const fileUploadProps = {
        name: "file",
        multiple: false,
        showUploadList: false,
        customRequest: async ({file, onSuccess, onError}) => {
            setImportLoading(true);
            try {
                const formData = new FormData();
                formData.append("file", file);
                const response = await stores.shiftStore.importShiftExcel(formData);

                if (response?.data?.errorExcel) {
                    const url = await b64DecodeToUrl(response.data.errorExcel);
                    downloadFile(url, `Hata_Raporu_${file.name}`);
                }

                if (response?.data?.errorExcel) {
                    stores.appStore.openErrorModal({
                        title: "Yükleme sırasında satır hataları bulundu",
                        content: "Hata raporu otomatik olarak indirildi."
                    });
                } else {
                    stores.appStore.openSuccessModal({
                        title: t("companyDetail.modal.fileUpload.success", {ns: "companyDetail"}),
                        content: (
                            response?.data?.validationMessages || []
                        ).map(validationMessage => (
                            <div key={validationMessage}>{validationMessage}</div>
                        ))
                    });
                }

                setReloadData();
                onSuccess(response);
            } catch (error) {
                message.error(
                    t("companyDetail.modal.fileUpload.error", {
                        ns: "companyDetail",
                        filename: file.name,
                        responseMessage: error?.message
                    })
                );
                onError(error);
            } finally {
                setImportLoading(false);
            }
        }
    };

    const handleExportExcel = async () => {
        const fileName = `${t("screens.shiftManagement", {ns: "common"})}.xlsx`;

        // Havuz ve sabit entegre listesi ayrı uçtan aktarılır; hangi liste olduğunu
        // integrationType belirler. Bu uç yalnız tarihe göre daraltma yapıyor.
        if (isPoolWorkType(workType)) {
            const poolResponse = await stores.poolShiftStore.downloadPoolShiftExcelFile({
                date: moment(filters.date).format("YYYY-MM-DD"),
                integrationType: workType,
                status: filters.status
            });
            if (poolResponse) downloadFile(poolResponse, fileName);
            return;
        }

        const response = await stores.shiftStore.downloadShiftExcelFile({
            skip,
            limit,
            city: filters.city,
            company: filters.company,
            // Export ucu tek alt firma kabul ediyor; çoklu seçimde daraltma yapılmaz (tümü
            // dışa aktarılır) ki yanlış tek bir şube gönderilmesin.
            subcompany: filters.subcompany?.length === 1 ? filters.subcompany[0] : undefined,
            period: filters.period,
            startDate: filters.startDate,
            endDate: filters.endDate,
            searchText: filters.searchText,
            status: filters.status
        });
        downloadFile(response, fileName);
    };

    // İçe/dışa aktarım ve şablon indirme tek bir menüde toplanıyor (Anlık Operasyon
    // sayfasındaki işlem menüsüyle aynı kalıp).
    const excelMenu = (
        <Menu>
            {canExport && (
                <Menu.Item
                    key="export"
                    data-testid="exportWithExcelButton"
                    icon={<DownloadOutlined />}
                    onClick={handleExportExcel}
                >
                    {t("companyDetail.actions.exportWithExcel", {ns: "companyDetail"})}
                </Menu.Item>
            )}
            {canImport && (
                <Menu.Item key="import" icon={<UploadOutlined />} disabled={importLoading}>
                    <Upload {...fileUploadProps} accept=".xlsx,.xls,.csv,.tsv">
                        <span data-testid="importFromExcelButton">
                            {t("companyDetail.actions.importFromExcel", {ns: "companyDetail"})}
                        </span>
                    </Upload>
                </Menu.Item>
            )}
            {canImport && (
                <Menu.Item key="template" icon={<DownloadOutlined />}>
                    <a href={SHIFT_TEMPLATE_URL} data-testid="downloadExcelTemplete" download>
                        {t("companyDetail.actions.downloadExcelTemplate", {ns: "companyDetail"})}
                    </a>
                </Menu.Item>
            )}
        </Menu>
    );

    return (
        <ButtonsRow justify="end">
            <ShiftEntryModal
                visible={isShiftModalOpen}
                onCancel={() => setIsShiftModalOpen(false)}
                setIsModalOpen={setIsShiftModalOpen}
                setReloadData={setReloadData}
                workType={workType}
            />
            {(canExport || canImport) && (
                <Dropdown overlay={excelMenu} trigger={["click"]}>
                    <CustomButton data-testid="excelActionsButton" icon={<FileExcelOutlined />}>
                        {t("actions.excelActions", {ns: "common"})} <DownOutlined />
                    </CustomButton>
                </Dropdown>
            )}
            {canSeeObjections && (
                <WarningButton
                    data-testid="objectionsButton"
                    icon={<AuditOutlined />}
                    onClick={() => history.push("/reports/shift-score/objections")}
                >
                    {t("screens.shiftReportObjections", {ns: "common"})}
                </WarningButton>
            )}
            {stores.meStore.checkPermission("shiftManagement.create") && (
                <CustomButton
                    data-testid="addShiftButton"
                    onClick={() => setIsShiftModalOpen(true)}
                    type="primary"
                    icon={<PlusOutlined />}
                >
                    {t("actions.addShift", {ns: "common"})}
                </CustomButton>
            )}
        </ButtonsRow>
    );
};

export default ShiftManagementActions;
